import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
} from '@mui/material';
import { IAttribute } from './constants';

interface Props {
  data?: IAttribute;
  isLoading?: boolean;
  onSubmit: (payload: Partial<IAttribute>) => void;
  onClose: () => void;
}

// Create / edit form for an attribute. Note `default_weight` is intentionally
// NOT edited here — top-level attribute weights are owned by the Expert IQ page
// (a normalized budget that must sum to 1), so we never send it from this form:
// on create the backend defaults it to 0, and on edit we leave it untouched.
export const AttributeForm: FC<Props> = ({
  data,
  isLoading,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState(data?.name ?? '');
  const [slug, setSlug] = useState(data?.slug ?? '');
  const [order, setOrder] = useState<string>(String(data?.order ?? 0));
  const [touched, setTouched] = useState(false);

  const submit = () => {
    setTouched(true);
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      slug: slug.trim() || undefined,
      order: Number(order) || 0,
    });
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Typography fontFamily="CircularStdBlack" sx={{ fontSize: 22 }}>
          {data ? 'Edit' : 'Add'} Attribute
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-4 pt-2">
          <TextField
            label="Name"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
            error={touched && !name.trim()}
            helperText={touched && !name.trim() ? 'Name is required' : ''}
            fullWidth
          />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            helperText="Optional — generated from the name when left blank"
            fullWidth
          />
          <TextField
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={isLoading}>
          {isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
